import type { StateCoverageEntry } from '../../types';

interface TooltipPosition {
  x: number;
  y: number;
}

interface StateTooltipProps {
  position: TooltipPosition;
  stateCode: string;
  coverage: StateCoverageEntry | null;
}

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia', FL: 'Florida',
  GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana',
  IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine',
  MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
  MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire',
  NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island',
  SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
  VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin',
  WY: 'Wyoming',
};

export default function StateTooltip({ position, stateCode, coverage }: StateTooltipProps) {
  const stateName = STATE_NAMES[stateCode] ?? stateCode;

  return (
    <div
      className="absolute z-50 pointer-events-none bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 text-sm min-w-[180px] max-w-[280px]"
      style={{
        left: position.x + 12,
        top: position.y - 10,
        transform: 'translateY(-50%)',
      }}
    >
      <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1.5 flex items-center gap-2">
        {coverage && (
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: coverage.regionColor }}
          />
        )}
        {stateName}
      </div>

      {!coverage ? (
        <div className="text-gray-400 dark:text-gray-500 text-xs italic">No coverage — open territory</div>
      ) : coverage.territories.length === 1 ? (
        // Single territory — simple display
        <>
          <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">
            Region: <span className="text-gray-700 dark:text-gray-300">{coverage.regionName}</span>
          </div>
          <div className="text-gray-500 dark:text-gray-400 text-xs mb-0.5">
            Territory: <span className="text-gray-700 dark:text-gray-300">{coverage.territories[0].name}</span>
          </div>
          <div className="text-gray-500 dark:text-gray-400 text-xs">
            Rep:{' '}
            <span className={coverage.territories[0].repName ? 'text-gray-700 dark:text-gray-300' : 'italic text-gray-400 dark:text-gray-500'}>
              {coverage.territories[0].repName ?? 'Unassigned'}
            </span>
          </div>
        </>
      ) : (
        // Multiple sub-territories (e.g., California, Florida)
        <>
          <div className="text-gray-500 dark:text-gray-400 text-xs mb-1.5">
            Region: <span className="text-gray-700 dark:text-gray-300">{coverage.regionName}</span>
            <span className="ml-1 text-gray-400 dark:text-gray-500">
              ({coverage.territories.length} sub-territories)
            </span>
          </div>
          <div className="space-y-1 max-h-[150px] overflow-y-auto">
            {coverage.territories.slice(0, 8).map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-2 text-xs">
                <span className="text-gray-700 dark:text-gray-300 truncate">{t.name}</span>
                <span className={`flex-shrink-0 ${t.repName ? 'text-gray-500 dark:text-gray-400' : 'italic text-gray-400 dark:text-gray-500'}`}>
                  {t.repName ?? 'Open'}
                </span>
              </div>
            ))}
            {coverage.territories.length > 8 && (
              <div className="text-xs text-gray-400 dark:text-gray-500 italic">
                +{coverage.territories.length - 8} more...
              </div>
            )}
          </div>
          <div className="mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400 dark:text-gray-500">
            {coverage.territories.filter(t => t.repName).length} assigned, {coverage.territories.filter(t => !t.repName).length} open
          </div>
        </>
      )}
    </div>
  );
}
